import * as React from "react";
import { Mic, MicOff } from "lucide-react";
import { cn } from "@/lib/utils";
import { useVoiceInput } from "@/hooks/use-voice-input";

export type VoiceInputTone = "neutral" | "rose" | "teal" | "indigo" | "violet" | "amber";

export interface VoiceInputButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "onChange" | "onError"> {
  /** Called whenever finalized speech is produced. */
  onTranscript: (chunk: string) => void;
  /** Optional live interim transcript callback. */
  onInterim?: (interim: string) => void;
  /** Variant for color theme — defaults to neutral. */
  tone?: VoiceInputTone;
  /** Size — defaults to "sm". */
  size?: "xs" | "sm" | "md";
  /** Label for screen readers; defaults to "Voice input". */
  label?: string;
  /** Hide entirely when not supported. Defaults to true. */
  hideIfUnsupported?: boolean;
  /** Language code, default en-GB. */
  lang?: string;
}

const toneClasses: Record<NonNullable<VoiceInputButtonProps["tone"]>, { idle: string; active: string }> = {
  neutral: {
    idle: "text-slate-400 hover:text-slate-700 hover:bg-slate-100",
    active: "text-white bg-slate-700 hover:bg-slate-800",
  },
  rose: {
    idle: "text-slate-400 hover:text-rose-600 hover:bg-rose-50",
    active: "text-white bg-rose-500 hover:bg-rose-600",
  },
  teal: {
    idle: "text-slate-400 hover:text-teal-700 hover:bg-teal-50",
    active: "text-white bg-teal-600 hover:bg-teal-700",
  },
  indigo: {
    idle: "text-slate-400 hover:text-indigo-700 hover:bg-indigo-50",
    active: "text-white bg-indigo-600 hover:bg-indigo-700",
  },
  violet: {
    idle: "text-slate-400 hover:text-violet-700 hover:bg-violet-50",
    active: "text-white bg-violet-600 hover:bg-violet-700",
  },
  amber: {
    idle: "text-slate-400 hover:text-amber-700 hover:bg-amber-50",
    active: "text-white bg-amber-600 hover:bg-amber-700",
  },
};

const sizeClasses: Record<NonNullable<VoiceInputButtonProps["size"]>, string> = {
  xs: "h-7 w-7",
  sm: "h-8 w-8",
  md: "h-10 w-10",
};

const iconSize: Record<NonNullable<VoiceInputButtonProps["size"]>, string> = {
  xs: "h-3.5 w-3.5",
  sm: "h-4 w-4",
  md: "h-5 w-5",
};

export const VoiceInputButton = React.forwardRef<HTMLButtonElement, VoiceInputButtonProps>(
  function VoiceInputButton(
    {
      onTranscript,
      onInterim,
      tone = "neutral",
      size = "sm",
      label = "Voice input",
      hideIfUnsupported = true,
      lang,
      className,
      disabled,
      title,
      ...rest
    },
    ref,
  ) {
    const { supported, listening, error, toggle } = useVoiceInput({ onTranscript, onInterim, lang });

    if (!supported && hideIfUnsupported) return null;

    const Icon = listening ? MicOff : Mic;
    const palette = toneClasses[tone];

    return (
      <button
        ref={ref}
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          toggle();
        }}
        disabled={disabled || !supported}
        aria-pressed={listening}
        aria-label={listening ? `${label} — stop listening` : label}
        title={
          title ??
          (!supported
            ? "Voice input not supported in this browser"
            : error
              ? error
              : listening
                ? "Stop dictation"
                : "Start dictation")
        }
        className={cn(
          "inline-flex items-center justify-center rounded-full transition-all shrink-0",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-slate-400",
          sizeClasses[size],
          listening ? palette.active : palette.idle,
          listening && "animate-pulse shadow-md",
          disabled && "opacity-40 cursor-not-allowed",
          className,
        )}
        {...rest}
      >
        <Icon className={iconSize[size]} />
        <span className="sr-only">{listening ? "Stop voice input" : "Start voice input"}</span>
      </button>
    );
  },
);
